import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useVisibilityAwareInterval } from '../../hooks/useVisibilityAwareInterval';
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  InformationCircleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import type { ColumnDef } from '@tanstack/react-table';
import { TanTable } from '../../components/TanTable';
import { Drawer } from '../../components/ui/Drawer';
import adminService, {
  AdminAuditLog,
  AdminAuditLogsStatsResponse,
} from '../../services/admin.service';
import {
  LOG_FETCH_ALL_LIMIT,
  LOG_AUTO_REFRESH_MS,
  DetailField,
  NONE,
  formatJsonMaybe,
} from './logPageShared';

/**
 * Logging → Admin Audit Logs.
 *
 * Pull-all + TanTable virtualization, detail in a slide-out drawer — same
 * pattern as System Logs. Request params/body, user agent and bypass reason
 * live in the drawer; row cells stay single-line for the virtualizer.
 *
 * Note the old page defaulted its server-side Sensitive filter to "false", so
 * sensitive operations were hidden until you changed the dropdown. Everything is
 * now loaded and Sensitive is a column filter, so those rows are visible by
 * default — they are the ones worth seeing on an audit page.
 */

const getActionBadgeColor = (action: string) => {
  if (action.includes('role_assignment') || action.includes('role_revocation')) {
    return 'bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800';
  }
  if (action.includes('permission') || action.includes('impersonation')) {
    return 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800';
  }
  if (action.includes('delete') || action.includes('revoke')) {
    return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800';
  }
  return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800';
};

const statusColor = (status?: number) => {
  if (status == null) return 'text-gray-400';
  if (status >= 500) return 'text-red-600 dark:text-red-400';
  if (status >= 400) return 'text-orange-600 dark:text-orange-400';
  return 'text-green-600 dark:text-green-400';
};

const formatTimestamp = (timestamp: string) => {
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? timestamp : date.toLocaleString();
};

const AuditLogs: React.FC = () => {
  const [logs, setLogs] = useState<AdminAuditLog[]>([]);
  const [stats, setStats] = useState<AdminAuditLogsStatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalLogs, setTotalLogs] = useState(0);
  const [selected, setSelected] = useState<AdminAuditLog | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const logsAbortRef = useRef<AbortController | null>(null);
  const statsAbortRef = useRef<AbortController | null>(null);

  const fetchLogs = useCallback(async () => {
    logsAbortRef.current?.abort();
    const controller = new AbortController();
    logsAbortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const response = await adminService.getAuditLogs(
        { limit: LOG_FETCH_ALL_LIMIT },
        controller.signal
      );
      setLogs(response.logs);
      setTotalLogs(response.total);
    } catch (err: any) {
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') return;
      setError(err.response?.data?.detail || 'Failed to fetch audit logs');
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    statsAbortRef.current?.abort();
    const controller = new AbortController();
    statsAbortRef.current = controller;

    try {
      setStats(await adminService.getAuditLogsStats(controller.signal));
    } catch (err: any) {
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch audit log stats:', err);
    }
  }, []);

  const refresh = useCallback(async () => {
    await fetchLogs();
    await fetchStats();
  }, [fetchLogs, fetchStats]);

  useEffect(() => {
    refresh();

    return () => {
      logsAbortRef.current?.abort();
      statsAbortRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useVisibilityAwareInterval(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, autoRefresh ? LOG_AUTO_REFRESH_MS : null);

  const columns = useMemo<ColumnDef<AdminAuditLog>[]>(
    () => [
      {
        accessorKey: 'created_at',
        header: 'Timestamp',
        cell: (info) => (
          <span className="whitespace-nowrap">{formatTimestamp(info.getValue() as string)}</span>
        ),
        size: 175,
      },
      {
        accessorKey: 'admin_email',
        header: 'Admin',
        cell: (info) => {
          const v = info.getValue() as string;
          return (
            <span className="block truncate" title={v}>
              {v}
            </span>
          );
        },
        size: 220,
      },
      {
        accessorKey: 'action',
        header: 'Action',
        cell: (info) => {
          const v = info.getValue() as string;
          return (
            <span
              className={`px-2 py-0.5 rounded text-xs font-medium border inline-block max-w-full truncate ${getActionBadgeColor(v)}`}
              title={v}
            >
              {v}
            </span>
          );
        },
        size: 260,
      },
      {
        id: 'endpoint',
        accessorFn: (row) =>
          row.endpoint ? `${row.http_method || ''} ${row.endpoint}`.trim() : '',
        header: 'Endpoint',
        cell: (info) => {
          const v = info.getValue() as string;
          if (!v) return <span className="text-gray-400">—</span>;
          return (
            <span className="block truncate" title={v}>
              {v}
            </span>
          );
        },
        size: 320,
      },
      {
        accessorKey: 'target_user_email',
        header: 'Target User',
        cell: (info) => {
          const v = info.getValue() as string | undefined;
          if (!v) return <span className="text-gray-400">—</span>;
          return (
            <span className="block truncate" title={v}>
              {v}
            </span>
          );
        },
        size: 200,
      },
      {
        // accessorFn gives the dropdown filter "Yes"/"No" instead of true/false.
        id: 'is_sensitive_operation',
        accessorFn: (row) => (row.is_sensitive_operation ? 'Yes' : 'No'),
        header: 'Sensitive',
        cell: (info) =>
          info.row.original.is_sensitive_operation ? (
            <span className="px-2 py-0.5 rounded text-xs font-medium border bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800 inline-flex items-center gap-1">
              <ExclamationTriangleIcon className="w-3 h-3" />
              SENSITIVE
            </span>
          ) : (
            <span className="text-gray-400">—</span>
          ),
        size: 130,
      },
      {
        accessorKey: 'response_status',
        header: 'Status',
        cell: (info) => {
          const v = info.getValue() as number | undefined;
          return (
            <span className={statusColor(v)}>{v ?? <span className="text-gray-400">—</span>}</span>
          );
        },
        size: 90,
      },
    ],
    []
  );

  const truncated = totalLogs > logs.length;

  return (
    <div className="p-4 pb-24">
      {/* Header */}
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheckIcon className="w-7 h-7 text-blue-600" />
            <h1 className="text-xl font-bold text-heading">Admin Audit Logs</h1>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Global administrator actions and permission bypasses. Click any row for the request
            detail.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={refresh}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            title={`Auto-refresh every ${LOG_AUTO_REFRESH_MS / 1000}s`}
            className={`px-3 py-2 rounded-md border text-sm ${
              autoRefresh
                ? 'bg-green-50 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-800 dark:text-green-300'
                : 'bg-gray-50 border-gray-300 text-label dark:bg-gray-700 dark:border-gray-600'
            }`}
          >
            Auto: {autoRefresh ? 'ON' : 'OFF'}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-panel rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Logs</div>
            <div className="text-2xl font-bold text-heading">
              {stats.total_logs.toLocaleString()}
            </div>
          </div>
          <div className="bg-panel rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
              Sensitive Operations
            </div>
            <div className="text-2xl font-bold text-red-600">{stats.sensitive_operations}</div>
          </div>
          <div className="bg-panel rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Actions (24h)</div>
            <div className="text-2xl font-bold text-blue-600">{stats.recent_actions_24h}</div>
          </div>
          <div className="bg-panel rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Sensitive (24h)</div>
            <div className="text-2xl font-bold text-orange-600">{stats.recent_sensitive_24h}</div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3 mb-4">
          <div className="flex items-center gap-2 text-red-800 dark:text-red-300">
            <XCircleIcon className="w-5 h-5" />
            <span className="font-medium">{error}</span>
          </div>
        </div>
      )}

      {truncated && (
        <div className="mb-2 px-3 py-1.5 text-xs text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded">
          Showing the {logs.length.toLocaleString()} most recent of{' '}
          {totalLogs.toLocaleString()} entries.
        </div>
      )}

      <TanTable
        data={logs}
        columns={columns}
        loading={loading}
        settingsKey="admin-audit-logs-table"
        fieldName="admin-audit-logs-table"
        cardTitle="Admin Actions"
        searchPlaceholder="Search admin, action, endpoint…"
        enableSearch={true}
        enableColumnFilters={true}
        enableShowHideCols={true}
        enableReset={true}
        enableExcelExport={true}
        enableZebraStriping={true}
        showGridLines={true}
        columnFilterTypes={{
          created_at: 'text',
          admin_email: 'text',
          endpoint: 'text',
          target_user_email: 'text',
          response_status: 'text',
        }}
        defaultSorting={[{ id: 'created_at', desc: true }]}
        onRowClick={(row: AdminAuditLog) => setSelected(row)}
      />

      <Drawer
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        maxWidth="xl"
        width="56rem"
        title={
          selected ? (
            <div className="flex items-center gap-2">
              {selected.is_sensitive_operation && (
                <span className="px-2 py-0.5 rounded text-xs font-medium border bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800">
                  SENSITIVE
                </span>
              )}
              <span className="truncate">{selected.action}</span>
              <span className="text-xs text-muted shrink-0">
                {formatTimestamp(selected.created_at)}
              </span>
            </div>
          ) : (
            'Admin Action'
          )
        }
      >
        {selected && (
          <div className="space-y-5 text-sm">
            <section>
              <div className="text-xs font-semibold text-muted uppercase tracking-wide mb-2">
                Action
              </div>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                <DetailField label="Action">{selected.action}</DetailField>
                <DetailField label="Entry ID">{selected.id}</DetailField>
                <DetailField label="Admin">{selected.admin_email}</DetailField>
                <DetailField label="Target User">
                  {selected.target_user_email || NONE}
                </DetailField>
                <DetailField label="Endpoint">
                  {selected.endpoint ? (
                    <>
                      {selected.http_method} {selected.endpoint}
                    </>
                  ) : (
                    NONE
                  )}
                </DetailField>
                <DetailField label="Response Status">
                  <span className={statusColor(selected.response_status)}>
                    {selected.response_status ?? '—'}
                  </span>
                </DetailField>
                <DetailField label="Resource">{selected.resource || NONE}</DetailField>
                <DetailField label="Permission Action">
                  {selected.permission_action || NONE}
                </DetailField>
                <DetailField label="IP Address">{selected.ip_address || NONE}</DetailField>
                <DetailField label="Timestamp">
                  {formatTimestamp(selected.created_at)}
                </DetailField>
              </dl>
            </section>

            {selected.bypass_reason && (
              <section>
                <div className="text-xs font-semibold text-muted uppercase tracking-wide mb-2">
                  Bypass Reason
                </div>
                <div className="text-sm text-heading whitespace-pre-wrap break-words">
                  {selected.bypass_reason}
                </div>
              </section>
            )}

            {selected.request_params && (
              <section>
                <div className="text-xs font-semibold text-muted uppercase tracking-wide mb-2">
                  Request Parameters
                </div>
                <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded overflow-x-auto">
                  {formatJsonMaybe(selected.request_params)}
                </pre>
              </section>
            )}

            {selected.request_body && (
              <section>
                <div className="text-xs font-semibold text-muted uppercase tracking-wide mb-2">
                  Request Body
                </div>
                <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded overflow-x-auto">
                  {formatJsonMaybe(selected.request_body)}
                </pre>
              </section>
            )}

            {selected.user_agent && (
              <section>
                <div className="text-xs font-semibold text-muted uppercase tracking-wide mb-2">
                  User Agent
                </div>
                <div className="text-xs text-heading break-all">
                  {selected.user_agent}
                </div>
              </section>
            )}
          </div>
        )}
      </Drawer>

      {logs.length === 0 && !loading && (
        <div className="text-center py-12">
          <InformationCircleIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 dark:text-gray-400">No audit logs found</p>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;
